/** Erro devolvido pela API, já com a mensagem em português para mostrar ao usuário. */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }

  /** Mensagem do campo indicado, quando o backend a devolveu. */
  doCampo(campo: string): string | undefined {
    if (!Array.isArray(this.detalhes)) return undefined;
    return (this.detalhes as { campo?: string; mensagem?: string }[]).find((d) => d.campo === campo)?.mensagem;
  }
}

/** Avisado quando a sessão cai (401): o app volta para o login. */
export const EVENTO_SESSAO_EXPIRADA = 'precificador:sessao-expirada';

async function erroDaResposta(resposta: Response, caminho: string): Promise<ErroApi> {
  const corpo = await resposta.json().catch(() => null);
  const erro = new ErroApi(
    resposta.status,
    corpo?.codigo ?? 'ERRO',
    corpo?.mensagem ?? `Erro ${resposta.status} no servidor.`,
    corpo?.detalhes,
  );
  if (resposta.status === 401 && !caminho.startsWith('/auth/login')) {
    window.dispatchEvent(new CustomEvent(EVENTO_SESSAO_EXPIRADA));
  }
  return erro;
}

async function chamar(caminho: string, init: RequestInit): Promise<Response> {
  let resposta: Response;
  try {
    resposta = await fetch(`/api${caminho}`, { credentials: 'same-origin', ...init });
  } catch {
    throw new ErroApi(0, 'SEM_CONEXAO', 'Sem conexão com o servidor. Confira a rede e tente de novo.');
  }
  if (!resposta.ok) throw await erroDaResposta(resposta, caminho);
  return resposta;
}

export async function api<T>(caminho: string, opcoes: { metodo?: string; corpo?: unknown } = {}): Promise<T> {
  const resposta = await chamar(caminho, {
    method: opcoes.metodo ?? 'GET',
    headers: opcoes.corpo !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: opcoes.corpo !== undefined ? JSON.stringify(opcoes.corpo) : undefined,
  });
  if (resposta.status === 204) return undefined as T;
  return (await resposta.json()) as T;
}

/** Envia um arquivo cru (CSV/XLSX) e lê a resposta JSON. */
export async function enviarArquivo<T>(caminho: string, arquivo: File): Promise<T> {
  const resposta = await chamar(caminho, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream', 'X-Nome-Arquivo': encodeURIComponent(arquivo.name) },
    body: arquivo,
  });
  return (await resposta.json()) as T;
}

/** Baixa um arquivo (PDF/XLSX) e dispara o "salvar como" com o nome enviado pelo servidor. */
export async function baixar(caminho: string, nomePadrao = 'arquivo'): Promise<void> {
  const resposta = await chamar(caminho, { method: 'GET' });
  const blob = await resposta.blob();
  const cabecalho = resposta.headers.get('Content-Disposition') ?? '';
  const nome = /filename="?([^";]+)"?/.exec(cabecalho)?.[1] ?? nomePadrao;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
